import React, { useState } from 'react';
import { BubbleSelectBlock, Modal, Services, Text, i18n, tokens } from "../../index";
import FormControl from "@mui/material/FormControl";
import * as dates from "@simplybenefits/sb-dates";
import englishCheque from '../../assets/void-cheque.png';
import frenchCheque from '../../assets/french-void-cheque.jpg';
import EftDetailsForm from './EftDetailsForm.js';

/**
 * Component for editing banking details.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {Function} props.showPage - Function to switch between pages.
 * @param {Object} props.currentBankingDetails - Current banking details.
 * @param {Object} props.meta - Metadata related to banking details.
 * @param {Function} props.saveNewBankingDetails - Function to save new banking details.
 * @param {Object} props.whitelabel - Whitelabel configuration.
 * @returns {JSX.Element} JSX element representing the banking details editing form.
 */
const EditBankingDetails = ({
  modalOpen = true,
  onBack,
  showModal,
  currentBankingDetails,
  meta,
  saveNewBankingDetails,
  close,
  whitelabel,
  bankingDetailsVerifying,
  validateBankingDetails,
  accountLevelBankingDetailsView = false,
  closeModalAfterSaving = false,
  isModal = true,
  loader
}) => {
  const hasDualBankAccountSetup = accountLevelBankingDetailsView ? false : currentBankingDetails?.default && currentBankingDetails?.paymentsOnly?.accountNum && currentBankingDetails?.paymentsOnly?.accountNum.length > 0;
  const chequeImage = i18n.locale === "fr" ? frenchCheque : englishCheque;
  const isMemberPay = meta?.divisionPaymentType === "member";
  const [eftDetails, setEftDetails] = useState({
    useSameBankAccount: !hasDualBankAccountSetup,
    default: {
      accountHolder: '',
      institutionName: '',
      institutionAddress: '',
      institutionNum: '',
      transitNum: '',
      accountNum: '',
      isValidated: false,
      accepted: false,
      timestampOfAcceptance: null,
      accountHolderError: false,
      institutionNumError: false,
      transitNumError: false,
      accountNumError: false,
      acceptedError: false,
      branchValidationError: false
    },
    paymentsOnly: {
      accountHolder: '',
      institutionName: '',
      institutionAddress: '',
      institutionNum: '',
      transitNum: '',
      accountNum: '',
      isValidated: false,
      accepted: false,
      timestampOfAcceptance: null,
      accountHolderError: false,
      institutionNumError: false,
      transitNumError: false,
      accountNumError: false,
      acceptedError: false,
      branchValidationError: false
    }
  });

  /**
   * Updates the state of the parent component with changes specific to a form type in the banking details.
   *
   * @param {string} formType - The type of form (e.g., "default", "paymentsOnly").
   * @param {Object} newState - The new state changes for the specified form type.
   * @param {string} newState.property - The specific property to update within the form type.
   * @param {*} newState.value - The new value to set for the specified property.
   * 
   */
  const _handleStateChanges = (formType, newState) => {
    setEftDetails(prevEftDetails => ({
      ...prevEftDetails,
      [formType]: {
        ...prevEftDetails[formType],
        ...newState
      }
    }));
  };

  /**
   * Handles changes in the input values for a specified form type in banking details.
   *
   * @function
   * @private
   * @param {string} formType - The type of form (e.g., "default", "paymentsOnly").
   * @param {string} type - The specific property to update within the form type.
   * @param {*} value - The new value to set for the specified property.
   * @param {Event} event - The event triggering the change.
   */
  const _handleChange = (formType, type, value, event) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    setEftDetails(prevEftDetails => {
      const updatedFormValue = {
        ...prevEftDetails[formType],
        [type]: value
      };
      if (type === 'accepted' && value === true) {
        updatedFormValue.timestampOfAcceptance = dates.getUTC();
      }
      return {
        ...prevEftDetails,
        [formType]: updatedFormValue
      };
    });
  };

  /**
   * Validates the specified form type in the banking details by checking input fields against predefined rules.
   *
   * @async
   * @function
   * @param {string} formType - The type of form to validate (e.g., "default", "paymentsOnly").
   * @returns {boolean} Returns true if the specified form is successfully validated, false otherwise.
   * 
   */
  const validateForm = async formType => {
    const form = eftDetails[formType];
    const accountHolderError = await Services.validateInput('bankAccountHolderName', form.accountHolder);
    const institutionNumError = await Services.validateInput('institutionNum', form.institutionNum);
    const transitNumError = await Services.validateInput('transitNum', form.transitNum);
    const accountNumError = await Services.validateInput('checkEmpty', form.accountNum);
    const acceptedError = form.accepted ? null : i18n.t(tokens.labels.banking.youMustAcceptTermsError);
    _handleStateChanges(formType, {
      accountHolderError: accountHolderError,
      institutionNumError: institutionNumError,
      transitNumError: transitNumError,
      accountNumError: accountNumError,
      acceptedError
    });
    return !accountHolderError && !institutionNumError && !transitNumError && !accountNumError && !acceptedError;
  };

  /**
   * Validates the banking details forms based on the current configuration.
   *
   * @async
   * @function
   * @private
   * @returns {Promise<boolean>} Resolves with true if the forms are successfully validated, false otherwise.
   */
  const _validateForms = async () => {
    if (eftDetails.useSameBankAccount) {
      return validateForm('default');
    }
    const isDefaultFormValid = await validateForm('default');
    const isPaymentsOnlyFormValid = await validateForm('paymentsOnly');
    return isDefaultFormValid && isPaymentsOnlyFormValid;
  };

  /**
   * Handles fetching and validating branch details for the provided institution and transit numbers.
   *
   * @async
   * @returns {boolean} Returns true if branch details are successfully validated, false otherwise.
   * @private
   */
  const validateBranchDetails = async formType => {
    const branchDetails = await validateBankingDetails(eftDetails[formType].institutionNum, eftDetails[formType].transitNum);
    if (branchDetails.data === null && branchDetails.isFound === false) {
      _handleChange(formType, 'branchValidationError', true);
      return false;
    }
    const {
      frenchInstitutionName,
      englishInstitutionName,
      branchDomicile,
      civicAddress,
      townCity,
      provinceCode,
      postalCode
    } = branchDetails.data;
    const validatedInstitutionName = i18n.locale === 'fr' ? frenchInstitutionName : englishInstitutionName;
    const validatedInstitutionAddress = [branchDomicile, civicAddress, townCity, provinceCode, postalCode].join(', ');
    _handleStateChanges(formType, {
      institutionName: validatedInstitutionName,
      institutionAddress: validatedInstitutionAddress,
      institutionNumError: null,
      transitNumError: null,
      branchValidationError: false,
      isValidated: true
    });
    return true;
  };

  /**
   * Validates banking details by checking form validations and branch details for different form types.
   *
   * @async
   * @private
   * @function
   * @returns {boolean} Returns true if banking details are successfully validated, false otherwise.
   * 
   */
  const _validateBankingDetails = async () => {
    const formsValidated = await _validateForms();
    if (!formsValidated) {
      return false;
    }
    if (eftDetails.useSameBankAccount) {
      return validateBranchDetails('default');
    }
    const isDefaultBranchValid = await validateBranchDetails('default');
    const isPaymentsOnlyBranchValid = await validateBranchDetails('paymentsOnly');
    return isDefaultBranchValid && isPaymentsOnlyBranchValid;
  };

  /**
   * Saves the banking details, triggers the `saveNewBankingDetails` function, and shows the 'confirmDetails' modal.
   *
   * @async
   * @private
   * @function
   * 
   */
  const _saveBankingDetailsAndContinue = async () => {
    const validatedBankingDetails = {
      useSameBankAccount: eftDetails.useSameBankAccount,
      default: {
        accountHolder: eftDetails.default.accountHolder,
        institutionName: eftDetails.default.institutionName,
        institutionAddress: eftDetails.default.institutionAddress,
        institutionNum: `${eftDetails.default.institutionNum}`,
        transitNum: `${eftDetails.default.transitNum}`,
        accountNum: `${eftDetails.default.accountNum}`,
        accepted: eftDetails.default.accepted,
        timestampOfAcceptance: Number(eftDetails.default.timestampOfAcceptance)
      }
    };
    if (!accountLevelBankingDetailsView && !eftDetails.useSameBankAccount) {
      validatedBankingDetails.paymentsOnly = {
        accountHolder: eftDetails.paymentsOnly.accountHolder,
        institutionName: eftDetails.paymentsOnly.institutionName,
        institutionAddress: eftDetails.paymentsOnly.institutionAddress,
        institutionNum: `${eftDetails.paymentsOnly.institutionNum}`,
        transitNum: `${eftDetails.paymentsOnly.transitNum}`,
        accountNum: `${eftDetails.paymentsOnly.accountNum}`,
        accepted: eftDetails.paymentsOnly.accepted,
        timestampOfAcceptance: Number(eftDetails.paymentsOnly.timestampOfAcceptance)
      };
    }
    await saveNewBankingDetails(validatedBankingDetails);
    showModal && showModal('confirmDetails');
    closeModalAfterSaving && close();
  };

  /**
   * Handles the onSave action for the banking details form:
   * - If using the same bank account and default details are not validated, triggers banking details validation.
   * - If not using the same account and either default or payments-only details are not validated, triggers banking details validation.
   * - Otherwise, saves the banking details and continues to the next step.
   *
   * @async
   * @function
   * @returns {Promise<void|null>} Resolves with null if validation is triggered, otherwise resolves after saving and continuing.
   * @throws {Error} Throws an error if any translation token is missing or if an unexpected condition occurs.
   * 
   */
  const onSave = async () => {
    const isSameBankAccount = eftDetails.useSameBankAccount;
    const isDefaultValidated = eftDetails.default.isValidated;
    const isPaymentsOnlyValidated = eftDetails.paymentsOnly.isValidated;
    if (isSameBankAccount && !isDefaultValidated) {
      return await _validateBankingDetails();
    } else if (!isSameBankAccount && (!isDefaultValidated || !isPaymentsOnlyValidated)) {
      return await _validateBankingDetails();
    }
    return await _saveBankingDetailsAndContinue();
  };

  /**
   * Generates the save button text based on the current state:
   * - If banking details are being verified, displays "Validating."
   * - If using the same bank account and default details are validated, shows "Save Banking Details."
   * - If not using the same account and both default and payments-only details are validated, shows "Save Banking Details."
   * - Otherwise, displays "Validate Banking Details."
   *
   * @function
   * @returns {string} The text for the save button.
   * 
   */
  const saveButtonText = bankingDetailsVerifying ? i18n.t(tokens.labels.banking.validating) : eftDetails.useSameBankAccount ? eftDetails.default.isValidated ? i18n.t(tokens.buttons.save) : i18n.t(tokens.labels.banking.validateBanking) : eftDetails.default.isValidated && eftDetails.paymentsOnly.isValidated ? i18n.t(tokens.buttons.save) : i18n.t(tokens.labels.banking.validateBanking);

  /**
   * 
   * @returns 
   */
  const editBankingForm = () => /*#__PURE__*/React.createElement(React.Fragment, null, meta?.paymentsSetup && meta?.paymentsSetup === 1 ? /*#__PURE__*/React.createElement(Text, {
    medium: true,
    light: true
  }, i18n.t(tokens.labels.banking.youHaveAlreadySubmittedBankingDetails)) : null, /*#__PURE__*/React.createElement(Text, {
    small: true,
    margin: "20px 0"
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 'bold',
      fontSize: 14
    }
  }, `${i18n.t(tokens.labels.banking.example)}: `), i18n.t(tokens.labels.banking.followingInformationFoundOnCheque)), /*#__PURE__*/React.createElement("img", {
    alt: "Example Cheque",
    src: chequeImage,
    style: {
      width: '75%',
      height: '75%',
      alignSelf: "center"
    }
  }), !accountLevelBankingDetailsView ? /*#__PURE__*/React.createElement(React.Fragment, null, meta?.paymentType !== 'CC' && /*#__PURE__*/React.createElement(FormControl, {
    component: "fieldset"
  }, /*#__PURE__*/React.createElement(Text, {
    small: true,
    margin: "30px 0 0 0"
  }, i18n.t(tokens.labels.banking.wouldYouLikeToUseTheSameBankAccounts)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    first: {
      title: i18n.t(tokens.buttons.yes),
      selected: eftDetails.useSameBankAccount === true,
      field: 'useSameBankAccountYes'
    },
    second: {
      title: i18n.t(tokens.buttons.no),
      selected: eftDetails.useSameBankAccount === false,
      field: 'useSameBankAccountNo'
    },
    handleChange: () => {
      setEftDetails(prevEftDetails => ({
        ...prevEftDetails,
        useSameBankAccount: !eftDetails.useSameBankAccount
      }));
    }
  }))) : null, accountLevelBankingDetailsView || eftDetails.useSameBankAccount ? /*#__PURE__*/React.createElement(EftDetailsForm, {
    formType: "default",
    eftDetails: eftDetails,
    handleStateChanges: _handleStateChanges,
    handleChange: _handleChange,
    whitelabel: whitelabel,
    accountLevelBankingDetailsView: accountLevelBankingDetailsView,
    isMemberPay: isMemberPay
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EftDetailsForm, {
    formType: "default",
    eftDetails: eftDetails,
    handleStateChanges: _handleStateChanges,
    handleChange: _handleChange,
    whitelabel: whitelabel,
    accountLevelBankingDetailsView: accountLevelBankingDetailsView,
    isMemberPay: isMemberPay
  }), /*#__PURE__*/React.createElement(EftDetailsForm, {
    formType: 'paymentsOnly',
    eftDetails: eftDetails,
    handleStateChanges: _handleStateChanges,
    handleChange: _handleChange,
    whitelabel: whitelabel,
    accountLevelBankingDetailsView: accountLevelBankingDetailsView,
    isMemberPay: isMemberPay
  })));
  if (!isModal) {
    return editBankingForm();
  }
  return /*#__PURE__*/React.createElement(Modal, {
    open: modalOpen,
    onBack: onBack,
    onClose: close,
    title: i18n.t(tokens.labels.banking.changeBankingDetails),
    primaryButton: {
      label: saveButtonText,
      onClick: onSave
    },
    loader: loader,
    medium: true
  }, editBankingForm());
};
export default EditBankingDetails;